import LZString from '/fp/compress.js';
import config from '/config.js';
import {
  ensureRsSessionId,
  getApiHost,
  sdkPostUrl,
  postJson,
  encryptBody,
  collectTimeoutMs,
  reportSdkCollectFailure,
  sdkConfigForCollect,
} from '/utils.js';
import { SDK_ERROR, errorCodeFrom } from '/errors.js';
import { LIFECYCLE_RUNNING, LIFECYCLE_PAUSED } from '/bb/constants.js';
import { getState, resetState, replaceState } from '/bb/state.js';
import {
  readSessionSnapshot,
  writeSessionSnapshot,
  clearSessionSnapshot,
  clearLifetime,
  priorSessionCount,
  bumpSessionLifetime,
} from '/bb/storage.js';
import { serializeMetrics } from '/bb/serializer.js';
import { ensureCurrentPage, nowMs } from '/bb/helpers.js';
import { finalizeOpenIntervals } from '/bb/finalize.js';
import { clearAutocompleteAcceptedValues } from '/bb/autocomplete.js';

import * as pointerL from '/bb/listeners/pointer.js';
import * as keyboardL from '/bb/listeners/keyboard.js';
import * as inputL from '/bb/listeners/input.js';
import * as rangeL from '/bb/listeners/range.js';
import * as fileL from '/bb/listeners/file.js';
import * as focusL from '/bb/listeners/focus.js';
import * as scrollL from '/bb/listeners/scroll.js';
import * as visibilityL from '/bb/listeners/visibility.js';
import * as navigationL from '/bb/listeners/navigation.js';
import * as formsL from '/bb/listeners/forms.js';
import * as sectionsL from '/bb/listeners/sections.js';

const LISTENERS = [
  pointerL,
  keyboardL,
  inputL,
  rangeL,
  fileL,
  focusL,
  scrollL,
  visibilityL,
  navigationL,
  formsL,
  sectionsL,
];

let pageHideHandler = null;
let visibilityPersistHandler = null;
let windowBlurHandler = null;
let windowFocusHandler = null;

function finalizeForegroundIntervals(s, t) {
  focusL.finalizeActiveFieldSession();
  finalizeOpenIntervals(s, t, { close: true });
  writeSessionSnapshot(s);
}

function resumeForegroundIntervals(s) {
  try {
    ensureCurrentPage();
    sectionsL.syncVisibleSections();
    formsL.syncVisibleForms();
    scrollL.syncViewportScale();
    focusL.syncActiveField();
  } catch (e) {
    /* ignore */
  }
  writeSessionSnapshot(s);
}

function attachAll() {
  for (const l of LISTENERS) {
    try {
      l.attach();
    } catch (e) {
      /* listener attach failure should not break the rest */
    }
  }
  if (typeof window !== 'undefined') {
    pageHideHandler = () => {
      const s = getState();
      if (s.lifecycle === LIFECYCLE_RUNNING || s.lifecycle === LIFECYCLE_PAUSED) {
        finalizeForegroundIntervals(s, Date.now());
      }
    };
    visibilityPersistHandler = () => {
      const s = getState();
      if (document.visibilityState === 'hidden') {
        if (s.lifecycle === LIFECYCLE_RUNNING) {
          finalizeForegroundIntervals(s, Date.now());
        }
      } else if (document.visibilityState === 'visible') {
        if (s.lifecycle === LIFECYCLE_RUNNING) {
          resumeForegroundIntervals(s);
        }
      }
    };
    windowBlurHandler = () => {
      const s = getState();
      if (s.lifecycle === LIFECYCLE_RUNNING) {
        finalizeForegroundIntervals(s, Date.now());
      }
    };
    windowFocusHandler = () => {
      const s = getState();
      if (s.lifecycle === LIFECYCLE_RUNNING && document.visibilityState === 'visible') {
        resumeForegroundIntervals(s);
      }
    };
    window.addEventListener('pagehide', pageHideHandler, { passive: true });
    window.addEventListener('blur', windowBlurHandler, { passive: true });
    window.addEventListener('focus', windowFocusHandler, { passive: true });
    document.addEventListener('visibilitychange', visibilityPersistHandler, { passive: true });
  }
}

function detachAll() {
  for (const l of LISTENERS) {
    try {
      l.detach();
    } catch (e) {
      /* ignore */
    }
  }
  if (typeof window !== 'undefined') {
    if (pageHideHandler) window.removeEventListener('pagehide', pageHideHandler);
    if (windowBlurHandler) window.removeEventListener('blur', windowBlurHandler);
    if (windowFocusHandler) window.removeEventListener('focus', windowFocusHandler);
    if (visibilityPersistHandler) document.removeEventListener('visibilitychange', visibilityPersistHandler);
    pageHideHandler = null;
    visibilityPersistHandler = null;
    windowBlurHandler = null;
    windowFocusHandler = null;
  }
}

/** Public lifecycle entry: start (or resume) BB tracking. */
export function bbStart() {
  if (typeof document === 'undefined') return Promise.resolve();
  const hydrated = readSessionSnapshot();
  const cur = getState();
  if (cur.lifecycle === LIFECYCLE_RUNNING) {
    return Promise.resolve();
  }
  if (hydrated && (hydrated.lifecycle === LIFECYCLE_RUNNING || hydrated.lifecycle === LIFECYCLE_PAUSED)) {
    replaceState(hydrated);
    const s = getState();
    if (s.lifecycle === LIFECYCLE_PAUSED && s.pausedAt > 0) {
      s.pausedTotalMs += Math.max(0, Date.now() - s.pausedAt);
      s.pausedAt = 0;
    }
    s.lifecycle = LIFECYCLE_RUNNING;
  } else {
    clearAutocompleteAcceptedValues();
    resetState();
    const s = getState();
    s.lifecycle = LIFECYCLE_RUNNING;
    s.startedAt = Date.now();
    s.session.priorSessionCount = priorSessionCount();
    const lifetime = bumpSessionLifetime(s.startedAt);
    s.session.activeDaysCount = lifetime.activeDaysCount;
  }
  attachAll();
  try {
    ensureCurrentPage();
    sectionsL.syncVisibleSections();
    focusL.syncActiveField();
  } catch (e) {
    /* ignore */
  }
  writeSessionSnapshot(getState());
  return Promise.resolve();
}

/** Manual pause: keep state, detach listeners. Resumable via bbStart. */
export function bbPause() {
  const s = getState();
  if (s.lifecycle !== LIFECYCLE_RUNNING) return Promise.resolve();
  const pausedAt = Date.now();
  focusL.finalizeActiveFieldSession();
  finalizeOpenIntervals(s, pausedAt, { close: true });
  s.lifecycle = LIFECYCLE_PAUSED;
  s.pausedAt = pausedAt;
  detachAll();
  writeSessionSnapshot(s);
  return Promise.resolve();
}

/** Hard stop: discard state, no POST. */
export function bbStop() {
  detachAll();
  clearAutocompleteAcceptedValues();
  resetState();
  clearSessionSnapshot();
  clearLifetime();
  return Promise.resolve();
}

/**
 * Finalize, POST, and clear all storage (success or failure).
 * @param {string} appId
 * @param {{ endpoints?: string[], timeout?: number, debug?: boolean }} [options]
 * @returns {Promise<{ session_id: string | null, error?: string }>}
 */
export async function bbSave(appId, options) {
  const saveStartedAt = Date.now();
  const s = getState();
  if (s.lifecycle !== LIFECYCLE_RUNNING && s.lifecycle !== LIFECYCLE_PAUSED) {
    return { session_id: null, error: SDK_ERROR.BB_NOT_RUNNING };
  }

  const sessionId = ensureRsSessionId();

  const bbUrl = () => {
    const base = getApiHost(options, config.defaultEndpoint);
    const bbPath = String(config.bbPath || 'bb').replace(/^\//, '');
    return sdkPostUrl(base, bbPath, appId);
  };

  let phase = 'finalize';
  let saveError = null;
  try {
    focusL.finalizeActiveFieldSession();
    s.endedAt = Date.now();
    if (s.lifecycle === LIFECYCLE_PAUSED && s.pausedAt > 0) {
      s.pausedTotalMs += Math.max(0, s.endedAt - s.pausedAt);
      s.pausedAt = 0;
    }
    finalizeOpenIntervals(s, s.endedAt, { close: true });

    phase = 'serialize';
    const metrics = serializeMetrics(s, s.endedAt);
    notifyBbDevToolsBeforeSave({ appId, sessionId, metrics, startedAt: saveStartedAt });
    const bbCompressed = encodeBbPayload(metrics);
    const version = typeof __SDK_VERSION__ !== 'undefined' && __SDK_VERSION__ != null ? __SDK_VERSION__ : null;
    const data = {
      bb: bbCompressed,
      session_id: sessionId,
      version: version,
      config: sdkConfigForCollect(options),
    };

    phase = 'encrypt';
    const payload = await encryptBody(appId, data);

    phase = 'post';
    const timeoutMs = collectTimeoutMs(options || {});
    await postJson(bbUrl(), payload, timeoutMs, 'bb');
  } catch (err) {
    saveError = err;
    // Await so bbSave does not resolve (and auto-save does not resume the form submit) until the
    // report is queued; preferBeacon makes that queued send survive the ensuing navigation.
    await reportSdkCollectFailure({
      appId,
      url: bbUrl(),
      timeoutMs: collectTimeoutMs(options || {}),
      err,
      sessionId,
      reportContext: { phase: phase, source: 'bb' },
      failLabel: 'bb',
      preferBeacon: true,
    });
  } finally {
    detachAll();
    resetState();
    clearAutocompleteAcceptedValues();
    clearSessionSnapshot();
  }

  const result = saveError
    ? { session_id: null, error: errorCodeFrom(saveError, SDK_ERROR.BB_FAILED) }
    : { session_id: sessionId };
  notifyBbDevToolsAfterSave({ appId, sessionId, result, durationMs: Date.now() - saveStartedAt });
  return result;
}

function encodeBbPayload(metrics) {
  return LZString.compressToBase64(JSON.stringify({ metrics }));
}

export function bbIsRunning() {
  const lifecycle = getState().lifecycle;
  return lifecycle === LIFECYCLE_RUNNING || lifecycle === LIFECYCLE_PAUSED;
}

export function bbSnapshot(appId, sessionId) {
  if (!bbIsRunning()) return null;
  const s = getState();
  const startedAt = Date.now();
  const view =
    s.lifecycle === LIFECYCLE_PAUSED && s.pausedAt > 0
      ? Object.assign({}, s, { pausedTotalMs: s.pausedTotalMs + Math.max(0, startedAt - s.pausedAt) })
      : s;
  const metrics = focusL.previewActiveFieldSession(function () {
    return serializeMetrics(view, startedAt);
  });
  notifyBbDevToolsBeforeSave({ appId, sessionId, metrics, startedAt });
  return { metrics, payload: encodeBbPayload(metrics), startedAt };
}

export function bbAfterSubmit(appId, sessionId, snapshot, result) {
  notifyBbDevToolsAfterSave({ appId, sessionId, result, durationMs: Date.now() - snapshot.startedAt });
}

function notifyBbDevToolsBeforeSave(payload) {
  const hook = getBbDevToolsHook();
  if (!hook || typeof hook.beforeSave !== 'function') return;

  try {
    hook.beforeSave(payload);
  } catch (e) {
    /* devtools must not affect save */
  }
}

function notifyBbDevToolsAfterSave(payload) {
  const hook = getBbDevToolsHook();
  if (!hook || typeof hook.afterSave !== 'function') return;

  try {
    hook.afterSave(payload);
  } catch (e) {
    /* devtools must not affect save */
  }
}

function getBbDevToolsHook() {
  if (typeof __BB_DEVTOOLS__ === 'undefined' || !__BB_DEVTOOLS__) return null;
  if (typeof window === 'undefined') return null;

  const hook = window.__RS_BB_DEVTOOLS_SAVE_HOOK__;
  return hook && typeof hook === 'object' ? hook : null;
}
